import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import AppPageTitle from "../../components/AppPageTitle";
import AppModal from "../../components/AppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { TemplatesTable, getTemplateHeaderFormat, listTemplateParamSlots, templateNeedsMedia } from "../../components/ScheduleModal/TemplatesTable";
import TemplateConversationPreview from "../../components/TemplateConversationPreview";
import { AuthContext } from "../../context/Auth/AuthContext";
import { ThemeContext } from "../../context/Theme/ThemeContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import apiV2 from "../../services/apiV2";
import toastError from "../../errors/toastError";
import { resolveUploadedFileUrl } from "../../helpers/resolveMediaUrl";

function pad(n) {
	return String(n).padStart(2, "0");
}

function toDateKey(d) {
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function startOfMonth(d) {
	return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d) {
	return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}

function eventStartDate(ev) {
	const raw = ev?.start?.dateTime || ev?.start?.date;
	if (!raw) return null;
	if (raw.length === 10) return raw;
	const d = new Date(raw);
	if (Number.isNaN(d.getTime())) return null;
	return toDateKey(d);
}

function formatEventTime(ev) {
	if (ev?.start?.date && !ev?.start?.dateTime) {
		return i18n.t("googleAgenda.allDay");
	}
	const raw = ev?.start?.dateTime;
	if (!raw) return "—";
	const d = new Date(raw);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function formatDayTitle(dayKey) {
	const [y, m, d] = dayKey.split("-").map(Number);
	const date = new Date(y, m - 1, d);
	return date.toLocaleDateString(undefined, {
		weekday: "long",
		day: "numeric",
		month: "long",
	});
}

function emptyForm(dayKey) {
	return {
		summary: "",
		description: "",
		allDay: false,
		start: `${dayKey}T09:00`,
		end: `${dayKey}T10:00`,
		startDate: dayKey,
		endDate: dayKey,
		contactId: null,
		contact: null,
	};
}

function renderContactLabel(contact) {
	if (!contact) return "";
	return [contact.name, contact.number].filter(Boolean).join(" - ");
}

function eventToForm(ev) {
	const allDay = Boolean(ev?.start?.date && !ev?.start?.dateTime);
	const contactId =
		ev?.contactId != null && Number(ev.contactId) > 0 ? Number(ev.contactId) : null;
	const contact =
		contactId || ev?.contactName || ev?.contactPhone
			? {
					id: contactId,
					name: ev.contactName || "",
					number: ev.contactPhone || "",
			  }
			: null;
	if (allDay) {
		return {
			summary: ev.summary || "",
			description: ev.description || "",
			allDay: true,
			start: "",
			end: "",
			startDate: ev.start.date,
			endDate: ev.end?.date || ev.start.date,
			contactId,
			contact,
		};
	}
	const start = ev.start?.dateTime ? new Date(ev.start.dateTime) : null;
	const end = ev.end?.dateTime ? new Date(ev.end.dateTime) : null;
	const toLocal = (d) =>
		d ? `${toDateKey(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}` : "";
	return {
		summary: ev.summary || "",
		description: ev.description || "",
		allDay: false,
		start: toLocal(start),
		end: toLocal(end),
		startDate: start ? toDateKey(start) : "",
		endDate: end ? toDateKey(end) : "",
		contactId,
		contact,
	};
}

function formToPayload(form) {
	const contactId =
		form.contactId != null && Number(form.contactId) > 0
			? Number(form.contactId)
			: null;
	if (form.allDay) {
		return {
			summary: form.summary.trim(),
			description: form.description.trim(),
			allDay: true,
			start: form.startDate,
			end: form.endDate,
			contactId,
		};
	}
	return {
		summary: form.summary.trim(),
		description: form.description.trim(),
		allDay: false,
		start: new Date(form.start).toISOString(),
		end: new Date(form.end).toISOString(),
		contactId,
	};
}

function buildMonthCells(cursor) {
	const first = startOfMonth(cursor);
	const startWeekday = first.getDay();
	const daysInMonth = endOfMonth(cursor).getDate();
	const cells = [];
	for (let i = 0; i < startWeekday; i += 1) cells.push(null);
	for (let day = 1; day <= daysInMonth; day += 1) {
		cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), day));
	}
	while (cells.length % 7 !== 0) cells.push(null);
	return cells;
}

export default function GoogleAgenda() {
	const { user } = useContext(AuthContext);
	const { darkMode } = useContext(ThemeContext);
	const todayKey = toDateKey(new Date());
	const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
	const [selectedDay, setSelectedDay] = useState(null);
	const [dayPanelOpen, setDayPanelOpen] = useState(false);
	const [connected, setConnected] = useState(null);
	const [email, setEmail] = useState("");
	const [events, setEvents] = useState([]);
	const [loading, setLoading] = useState(true);
	const [modalOpen, setModalOpen] = useState(false);
	const [saving, setSaving] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(() => emptyForm(todayKey));
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [slotMinutes, setSlotMinutes] = useState(30);
	const [slotCapacity, setSlotCapacity] = useState(1);
	const [minAdvanceMinutes, setMinAdvanceMinutes] = useState(0);
	const [notifyEnabled, setNotifyEnabled] = useState(false);
	const [notifyOffsetMinutes, setNotifyOffsetMinutes] = useState(0);
	const [notifyConnectionId, setNotifyConnectionId] = useState("");
	const [notifyBody, setNotifyBody] = useState("");
	const [notifyTemplateId, setNotifyTemplateId] = useState("");
	const [notifyTemplate, setNotifyTemplate] = useState(null);
	const [notifyTemplateParams, setNotifyTemplateParams] = useState([]);
	const [notifyMediaPath, setNotifyMediaPath] = useState("");
	const [uploadingNotifyMedia, setUploadingNotifyMedia] = useState(false);
	const [templateParamsLength, setTemplateParamsLength] = useState(0);
	const [connectionOptions, setConnectionOptions] = useState([]);
	const [savingSettings, setSavingSettings] = useState(false);
	const [contactQuery, setContactQuery] = useState("");
	const [contactOptions, setContactOptions] = useState([]);
	const [loadingContacts, setLoadingContacts] = useState(false);

	const SLOT_OPTIONS = [15, 20, 30, 45, 60, 90, 120];
	const ADVANCE_OPTIONS = [
		{ value: 0, labelKey: "advanceNone" },
		{ value: 30, labelKey: "advance30m" },
		{ value: 60, labelKey: "advance1h" },
		{ value: 120, labelKey: "advance2h" },
		{ value: 180, labelKey: "advance3h" },
		{ value: 240, labelKey: "advance4h" },
		{ value: 360, labelKey: "advance6h" },
		{ value: 720, labelKey: "advance12h" },
		{ value: 1440, labelKey: "advance1d" },
		{ value: 2880, labelKey: "advance2d" },
		{ value: 4320, labelKey: "advance3d" },
		{ value: 10080, labelKey: "advance7d" },
	];
	const NOTIFY_OFFSET_OPTIONS = [
		{ value: -1440, labelKey: "notify1dBefore" },
		{ value: -720, labelKey: "notify12hBefore" },
		{ value: -360, labelKey: "notify6hBefore" },
		{ value: -180, labelKey: "notify3hBefore" },
		{ value: -120, labelKey: "notify2hBefore" },
		{ value: -60, labelKey: "notify1hBefore" },
		{ value: -30, labelKey: "notify30mBefore" },
		{ value: 0, labelKey: "notifyAtTime" },
		{ value: 30, labelKey: "notify30mAfter" },
		{ value: 60, labelKey: "notify1hAfter" },
	];

	const monthLabel = useMemo(
		() =>
			cursor.toLocaleDateString(undefined, {
				month: "long",
				year: "numeric",
			}),
		[cursor]
	);

	const cells = useMemo(() => buildMonthCells(cursor), [cursor]);

	const eventsByDay = useMemo(() => {
		const map = {};
		events.forEach((ev) => {
			const key = eventStartDate(ev);
			if (!key) return;
			if (!map[key]) map[key] = [];
			map[key].push(ev);
		});
		return map;
	}, [events]);

	const dayEvents = selectedDay ? eventsByDay[selectedDay] || [] : [];

	const loadStatus = useCallback(async () => {
		const { data } = await apiV2.get("/google-calendar/status");
		setConnected(Boolean(data?.connected));
		setEmail(data?.email || "");
		return Boolean(data?.connected);
	}, []);

	const loadBookingSettings = useCallback(async () => {
		const { data } = await apiV2.get("/google-calendar/booking-settings");
		if (data?.slotMinutes) setSlotMinutes(Number(data.slotMinutes) || 30);
		if (data?.slotCapacity) setSlotCapacity(Number(data.slotCapacity) || 1);
		setMinAdvanceMinutes(
			Number.isFinite(Number(data?.minAdvanceMinutes))
				? Math.max(0, Number(data.minAdvanceMinutes))
				: 0
		);
		setNotifyEnabled(Boolean(data?.notifyEnabled));
		setNotifyOffsetMinutes(
			Number.isFinite(Number(data?.notifyOffsetMinutes))
				? Number(data.notifyOffsetMinutes)
				: 0
		);
		setNotifyConnectionId(
			data?.notifyConnectionId != null && Number(data.notifyConnectionId) > 0
				? String(data.notifyConnectionId)
				: ""
		);
		setNotifyBody(String(data?.notifyBody || ""));
		setNotifyTemplateId(String(data?.notifyTemplateId || ""));
		setNotifyTemplate(null);
		try {
			const parsed = JSON.parse(data?.notifyTemplateParams || "[]");
			setNotifyTemplateParams(Array.isArray(parsed) ? parsed : []);
		} catch {
			setNotifyTemplateParams([]);
		}
		setNotifyMediaPath(String(data?.notifyMediaPath || ""));
	}, []);

	const loadEvents = useCallback(async () => {
		const { data } = await apiV2.get("/google-calendar/events", {
			params: {
				timeMin: startOfMonth(cursor).toISOString(),
				timeMax: endOfMonth(cursor).toISOString(),
			},
		});
		setEvents(Array.isArray(data?.events) ? data.events : []);
	}, [cursor]);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			setLoading(true);
			try {
				const ok = await loadStatus();
				if (cancelled) return;
				if (ok) {
					await Promise.all([loadEvents(), loadBookingSettings()]);
				} else setEvents([]);
			} catch (err) {
				if (!cancelled) {
					toastError(err);
					toast.error(i18n.t("googleAgenda.loadError"));
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [loadStatus, loadEvents, loadBookingSettings]);

	useEffect(() => {
		if (!dayPanelOpen) return undefined;
		const onKey = (e) => {
			if (e.key === "Escape") setDayPanelOpen(false);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [dayPanelOpen]);

	useEffect(() => {
		if (!settingsOpen || user?.companyId == null) return undefined;
		let cancelled = false;
		(async () => {
			try {
				const { data } = await apiV2.get("/connections", {
					params: { companyId: user.companyId },
				});
				if (!cancelled) {
					setConnectionOptions(Array.isArray(data) ? data : []);
				}
			} catch (err) {
				if (!cancelled) {
					toastError(err);
					setConnectionOptions([]);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [settingsOpen, user?.companyId]);

	const whatsappConnections = useMemo(
		() =>
			connectionOptions.filter((c) => {
				const channel = String(c?.channel || "").toLowerCase();
				return (
					channel === "whatsapp" ||
					channel === "whatsappcloud" ||
					channel === "whatsappbusiness" ||
					channel === "whatsapp_business_account"
				);
			}),
		[connectionOptions]
	);

	const selectedNotifyConnection = useMemo(
		() =>
			whatsappConnections.find(
				(c) => String(c.id) === String(notifyConnectionId)
			) || null,
		[whatsappConnections, notifyConnectionId]
	);

	const notifyIsBusiness = useMemo(() => {
		const channel = String(selectedNotifyConnection?.channel || "").toLowerCase();
		return (
			channel === "whatsappcloud" ||
			channel === "whatsappbusiness" ||
			channel === "whatsapp_business_account"
		);
	}, [selectedNotifyConnection]);

	const notifyParamSlots = useMemo(
		() => listTemplateParamSlots(notifyTemplate?.components || []),
		[notifyTemplate]
	);
	const notifyHeaderFormat = getTemplateHeaderFormat(notifyTemplate);
	const notifyRequiresMedia = templateNeedsMedia(notifyHeaderFormat);
	const notifyMediaLabel =
		notifyHeaderFormat === "IMAGE"
			? "imagem"
			: notifyHeaderFormat === "VIDEO"
				? "vídeo"
				: notifyHeaderFormat === "DOCUMENT"
					? "documento"
					: "mídia";

	const handleNotifyTemplateResolved = useCallback((template) => {
		setNotifyTemplate(template || null);
	}, []);

	const handleNotifyMediaUpload = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;
		setUploadingNotifyMedia(true);
		try {
			const formData = new FormData();
			formData.append("file", file);
			const response = await api.post("/chatbot/node-upload-image", formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			setNotifyMediaPath(resolveUploadedFileUrl(file, response));
		} catch (err) {
			toastError(err);
		} finally {
			setUploadingNotifyMedia(false);
			event.target.value = "";
		}
	};

	const openDay = (key) => {
		setSelectedDay(key);
		setDayPanelOpen(true);
	};

	const closeDayPanel = () => setDayPanelOpen(false);

	const openCreate = (dayKey = selectedDay || todayKey) => {
		setEditing(null);
		const base = emptyForm(dayKey);
		const endHour = 9 + Math.floor(slotMinutes / 60);
		const endMin = slotMinutes % 60;
		base.end = `${dayKey}T${pad(endHour)}:${pad(endMin)}`;
		setForm(base);
		setContactQuery("");
		setContactOptions([]);
		setModalOpen(true);
	};

	const openEdit = async (ev) => {
		setEditing(ev);
		const nextForm = eventToForm(ev);
		setForm(nextForm);
		setContactQuery(renderContactLabel(nextForm.contact));
		setContactOptions([]);
		setModalOpen(true);
		if (!ev?.id) return;
		try {
			const { data } = await apiV2.get("/google-calendar/bookings", {
				params: { googleEventId: ev.id },
			});
			const booking = Array.isArray(data?.bookings)
				? data.bookings.find(
						(row) => String(row?.googleEventId || "") === String(ev.id)
				  ) || data.bookings[0]
				: null;
			if (!booking?.contactId) return;
			const contact = {
				id: booking.contactId,
				name: booking.contactName || "",
				number: booking.contactPhone || "",
			};
			setForm((prev) => ({
				...prev,
				contactId: booking.contactId,
				contact,
			}));
			setContactQuery(renderContactLabel(contact));
		} catch {
			/* contato opcional ao editar */
		}
	};

	useEffect(() => {
		if (!modalOpen) return undefined;
		const term = contactQuery.trim();
		if (
			term.length < 3 ||
			renderContactLabel(form.contact).toLowerCase() === term.toLowerCase()
		) {
			setContactOptions([]);
			setLoadingContacts(false);
			return undefined;
		}
		let cancelled = false;
		setLoadingContacts(true);
		const timer = setTimeout(async () => {
			try {
				const {
					data: { contacts: contactList },
				} = await api.get("contacts", { params: { searchParam: term } });
				if (cancelled) return;
				setContactOptions(
					(Array.isArray(contactList) ? contactList : []).map((item) => ({
						id: item.id,
						name: item.name,
						number: item.number,
					}))
				);
			} catch (err) {
				if (!cancelled) toastError(err);
			} finally {
				if (!cancelled) setLoadingContacts(false);
			}
		}, 400);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [modalOpen, contactQuery, form.contact]);

	const handleSaveSettings = async () => {
		if (notifyEnabled && !notifyConnectionId) {
			toast.error(i18n.t("googleAgenda.notifyConnectionEmpty"));
			return;
		}
		if (notifyEnabled && notifyIsBusiness && !notifyTemplateId) {
			toast.error(i18n.t("googleAgenda.notifyTemplateRequired"));
			return;
		}
		if (notifyEnabled && notifyIsBusiness && notifyRequiresMedia && !String(notifyMediaPath || "").trim()) {
			toast.error(i18n.t("googleAgenda.notifyMediaRequired"));
			return;
		}
		if (
			notifyEnabled &&
			notifyIsBusiness &&
			notifyParamSlots.length > 0 &&
			notifyParamSlots.some((_, index) => !String(notifyTemplateParams[index] ?? "").trim())
		) {
			toast.error(i18n.t("googleAgenda.notifyTemplateParamsRequired"));
			return;
		}
		if (notifyEnabled && !notifyIsBusiness && !notifyBody.trim()) {
			toast.error(i18n.t("googleAgenda.notifyBodyRequired"));
			return;
		}
		setSavingSettings(true);
		try {
			const { data } = await apiV2.put("/google-calendar/booking-settings", {
				slotMinutes: Number(slotMinutes),
				slotCapacity: Number(slotCapacity),
				minAdvanceMinutes: Number(minAdvanceMinutes),
				notifyEnabled,
				notifyOffsetMinutes: Number(notifyOffsetMinutes),
				notifyConnectionId: notifyConnectionId
					? Number(notifyConnectionId)
					: null,
				notifyBody: notifyBody.trim(),
				notifyTemplateId: notifyIsBusiness ? notifyTemplateId || "" : "",
				notifyTemplateParams: notifyIsBusiness
					? JSON.stringify(
							(notifyTemplateParams || []).slice(0, notifyParamSlots.length)
					  )
					: "[]",
				notifyMediaPath: notifyIsBusiness ? String(notifyMediaPath || "").trim() : "",
			});
			setSlotMinutes(Number(data?.slotMinutes) || slotMinutes);
			setSlotCapacity(Number(data?.slotCapacity) || slotCapacity);
			setMinAdvanceMinutes(
				Number.isFinite(Number(data?.minAdvanceMinutes))
					? Math.max(0, Number(data.minAdvanceMinutes))
					: minAdvanceMinutes
			);
			setNotifyEnabled(Boolean(data?.notifyEnabled));
			if (Number.isFinite(Number(data?.notifyOffsetMinutes))) {
				setNotifyOffsetMinutes(Number(data.notifyOffsetMinutes));
			}
			if (data?.notifyConnectionId != null) {
				setNotifyConnectionId(String(data.notifyConnectionId));
			}
			toast.success(i18n.t("googleAgenda.settingsSaveSuccess"));
			setSettingsOpen(false);
		} catch (err) {
			toastError(err);
			toast.error(i18n.t("googleAgenda.settingsSaveError"));
		} finally {
			setSavingSettings(false);
		}
	};

	const handleSave = async () => {
		if (!form.summary.trim()) return;
		setSaving(true);
		try {
			const payload = formToPayload(form);
			if (editing?.id) {
				await apiV2.put(
					`/google-calendar/events/${encodeURIComponent(editing.id)}`,
					payload
				);
			} else {
				await apiV2.post("/google-calendar/events", payload);
			}
			toast.success(i18n.t("googleAgenda.saveSuccess"));
			setModalOpen(false);
			await loadEvents();
		} catch (err) {
			toastError(err);
			toast.error(i18n.t("googleAgenda.saveError"));
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!deleteTarget?.id) return;
		try {
			await apiV2.delete(
				`/google-calendar/events/${encodeURIComponent(deleteTarget.id)}`
			);
			toast.success(i18n.t("googleAgenda.deleteSuccess"));
			setDeleteTarget(null);
			await loadEvents();
		} catch (err) {
			toastError(err);
			toast.error(i18n.t("googleAgenda.deleteError"));
		}
	};

	const weekDays = useMemo(() => {
		const base = new Date(2024, 0, 7);
		return Array.from({ length: 7 }, (_, i) => {
			const d = new Date(base);
			d.setDate(base.getDate() + i);
			return d.toLocaleDateString(undefined, { weekday: "short" });
		});
	}, []);

	const weekCount = Math.ceil(cells.length / 7);

	if (connected === false) {
		return (
			<div className="app-admin-page">
				<AppPageTitle
					title={i18n.t("googleAgenda.title")}
					subtitle={i18n.t("googleAgenda.subtitle")}
				/>
				<div className="card mx-auto max-w-lg">
					<div className="card-body py-10 text-center">
						<h3 className="mb-2 text-base font-semibold text-default-900 dark:text-default-50">
							{i18n.t("googleAgenda.notConnectedTitle")}
						</h3>
						<p className="mb-5 text-sm text-default-500">
							{i18n.t("googleAgenda.notConnectedHint")}
						</p>
						<Link to="/integrations" className="btn btn-sm bg-primary text-white">
							{i18n.t("googleAgenda.goToIntegrations")}
						</Link>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="app-admin-page relative !overflow-hidden">
			<div className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-3">
				<div>
					<h4 className="m-0 text-lg font-semibold text-default-900 dark:text-default-50">
						{i18n.t("googleAgenda.title")}
					</h4>
					{email ? (
						<p className="mt-0.5 mb-0 text-xs text-default-500">{email}</p>
					) : (
						<p className="mt-0.5 mb-0 text-xs text-default-500">
							{i18n.t("googleAgenda.subtitle")}
						</p>
					)}
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<button
						type="button"
						className="btn btn-sm border border-default-200 bg-white font-medium text-default-700 hover:bg-default-50 dark:border-default-700 dark:bg-default-900 dark:text-default-200 dark:hover:bg-default-800"
						onClick={() => setSettingsOpen(true)}
						disabled={loading || connected !== true}
					>
						{i18n.t("googleAgenda.settings")}
					</button>
					<Link
						to="/google-agenda/relatorio"
						className="btn btn-sm border border-primary/40 bg-primary/15 font-semibold text-primary hover:bg-primary/25 dark:border-primary/50 dark:bg-primary/20 dark:text-primary dark:hover:bg-primary/30"
					>
						{i18n.t("googleAgenda.report")}
					</Link>
					<button
						type="button"
						className="btn btn-sm bg-primary text-white"
						onClick={() => openCreate()}
						disabled={loading || connected !== true}
					>
						{i18n.t("googleAgenda.newEvent")}
					</button>
				</div>
			</div>

			<div className="flex min-h-0 flex-1 flex-col">
				<div className="mb-3 flex shrink-0 items-center justify-between gap-3">
					<div className="flex items-center gap-1">
						<button
							type="button"
							className="inline-flex size-8 items-center justify-center rounded-lg text-default-600 transition hover:bg-default-100 dark:text-default-300 dark:hover:bg-default-800"
							onClick={() => {
								setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
								closeDayPanel();
							}}
							aria-label={i18n.t("googleAgenda.prevMonth")}
						>
							‹
						</button>
						<h2 className="m-0 min-w-[10rem] text-center text-base font-semibold capitalize tracking-tight text-default-900 dark:text-default-50">
							{monthLabel}
						</h2>
						<button
							type="button"
							className="inline-flex size-8 items-center justify-center rounded-lg text-default-600 transition hover:bg-default-100 dark:text-default-300 dark:hover:bg-default-800"
							onClick={() => {
								setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
								closeDayPanel();
							}}
							aria-label={i18n.t("googleAgenda.nextMonth")}
						>
							›
						</button>
					</div>
					<div className="flex items-center gap-2">
						{loading && (
							<span className="text-xs text-default-400">
								{i18n.t("googleAgenda.loading")}
							</span>
						)}
						<button
							type="button"
							className="rounded-lg border border-default-200 px-2.5 py-1 text-xs font-medium text-default-700 transition hover:bg-default-50 dark:border-default-700 dark:text-default-200 dark:hover:bg-default-800"
							onClick={() => {
								const now = new Date();
								setCursor(startOfMonth(now));
								openDay(toDateKey(now));
							}}
						>
							{i18n.t("googleAgenda.today")}
						</button>
					</div>
				</div>

				<div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-default-200 bg-white dark:border-default-700 dark:bg-default-900">
					<div className="grid shrink-0 grid-cols-7 border-b border-default-100 dark:border-default-800">
						{weekDays.map((d) => (
							<div
								key={d}
								className="py-2.5 text-center text-[11px] font-medium uppercase tracking-wider text-default-400"
							>
								{d}
							</div>
						))}
					</div>

					<div
						className="grid min-h-0 flex-1 grid-cols-7"
						style={{
							gridTemplateRows: `repeat(${weekCount}, minmax(0, 1fr))`,
						}}
					>
						{cells.map((day, idx) => {
							if (!day) {
								return (
									<div
										key={`empty-${idx}`}
										className={`border-b border-default-100 bg-default-50/50 dark:border-default-800 dark:bg-default-950/25 ${
											(idx + 1) % 7 === 0 ? "" : "border-r"
										}`}
									/>
								);
							}
							const key = toDateKey(day);
							const dayList = eventsByDay[key] || [];
							const isSelected = dayPanelOpen && key === selectedDay;
							const isToday = key === todayKey;
							const previewLimit = weekCount <= 5 ? 3 : 2;
							const preview = dayList.slice(0, previewLimit);
							const extra = dayList.length - preview.length;
							const isLastCol = (idx + 1) % 7 === 0;

							return (
								<button
									type="button"
									key={key}
									onClick={() => openDay(key)}
									className={`relative z-0 flex h-full min-h-0 flex-col gap-1.5 border-b border-default-100 p-1.5 text-left transition duration-150 sm:p-2 dark:border-default-800 ${
										isLastCol ? "" : "border-r"
									} ${
										isSelected
											? "z-10 bg-primary/10 shadow-[inset_0_0_0_2px_var(--color-primary,#3b82f6)] dark:bg-primary/20"
											: "hover:bg-default-50 hover:shadow-[inset_0_0_0_1px_rgba(59,130,246,0.35)] dark:hover:bg-default-800/50"
									}`}
								>
									<div className="flex shrink-0 items-center justify-between gap-1">
										<span
											className={`inline-flex size-7 items-center justify-center rounded-lg text-[13px] font-semibold tabular-nums ${
												isSelected
													? "bg-primary text-white shadow-sm shadow-primary/30"
													: isToday
														? "bg-primary/15 text-primary ring-1 ring-primary/40"
														: "bg-default-100 text-default-700 dark:bg-default-800 dark:text-default-200"
											}`}
										>
											{day.getDate()}
										</span>
										{dayList.length > 0 && (
											<span
												className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
													isSelected
														? "bg-primary text-white"
														: "bg-primary/15 text-primary"
												}`}
											>
												{dayList.length}
											</span>
										)}
									</div>
									<div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-hidden">
										{preview.map((ev) => (
											<span
												key={ev.id}
												className={`block truncate rounded-md px-1 py-0.5 text-[11px] leading-snug ${
													isSelected
														? "bg-white/90 text-default-800 dark:bg-default-900/70 dark:text-default-100"
														: "text-default-700 dark:text-default-200"
												}`}
												style={
													isSelected
														? { borderLeft: "2px solid var(--color-primary, #3b82f6)" }
														: {
																background:
																	"color-mix(in srgb, var(--color-primary, #3b82f6) 14%, transparent)",
																borderLeft:
																	"2px solid var(--color-primary, #3b82f6)",
															}
												}
												title={ev.summary || ""}
											>
												{ev.summary || "—"}
											</span>
										))}
										{extra > 0 && (
											<span
												className={`px-1 text-[10px] font-medium ${
													isSelected ? "text-primary" : "text-default-400"
												}`}
											>
												+{extra}
											</span>
										)}
									</div>
								</button>
							);
						})}
					</div>
				</div>
			</div>

			<div
				className={`fixed inset-0 z-40 bg-black/45 backdrop-blur-[2px] transition-opacity duration-200 ${
					dayPanelOpen
						? "pointer-events-auto opacity-100"
						: "pointer-events-none opacity-0"
				}`}
				onClick={closeDayPanel}
				aria-hidden={!dayPanelOpen}
			/>
			<aside
				className={`fixed inset-y-0 right-0 z-50 flex w-[min(100%,28rem)] flex-col border-l border-default-200/80 bg-white shadow-[-12px_0_40px_rgba(15,23,42,0.18)] transition-transform duration-300 ease-out dark:border-default-700 dark:bg-default-900 dark:shadow-[-12px_0_40px_rgba(0,0,0,0.45)] ${
					dayPanelOpen ? "translate-x-0" : "translate-x-full"
				}`}
				aria-hidden={!dayPanelOpen}
			>
				<div className="relative overflow-hidden border-b border-primary/15 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent px-5 pb-4 pt-5 dark:from-primary/25 dark:via-primary/10">
					<div className="absolute inset-y-0 left-0 w-1 bg-primary" />
					<div className="flex items-start justify-between gap-3">
						<div className="flex min-w-0 items-start gap-3">
							{selectedDay && (
								<div className="flex size-12 shrink-0 flex-col items-center justify-center rounded-2xl bg-primary text-white shadow-md shadow-primary/25">
									<span className="text-[10px] font-semibold uppercase leading-none opacity-90">
										{(() => {
											const [y, m, d] = selectedDay.split("-").map(Number);
											return new Date(y, m - 1, d).toLocaleDateString(
												undefined,
												{ weekday: "short" }
											);
										})()}
									</span>
									<span className="mt-0.5 text-xl font-bold leading-none tabular-nums">
										{Number(selectedDay.split("-")[2])}
									</span>
								</div>
							)}
							<div className="min-w-0 pt-0.5">
								<p className="m-0 text-[11px] font-semibold uppercase tracking-wider text-primary">
									{i18n.t("googleAgenda.selectedDay")}
								</p>
								<h2 className="mt-1 mb-0 text-base font-semibold capitalize leading-snug text-default-900 dark:text-default-50">
									{selectedDay ? formatDayTitle(selectedDay) : ""}
								</h2>
								<p className="mt-1 mb-0 text-xs text-default-500">
									{dayEvents.length === 0
										? i18n.t("googleAgenda.noEvents")
										: `${dayEvents.length} ${
												dayEvents.length === 1
													? i18n.t("googleAgenda.eventSingular")
													: i18n.t("googleAgenda.eventPlural")
											}`}
								</p>
							</div>
						</div>
						<button
							type="button"
							className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl border border-default-200 bg-white/80 text-default-600 shadow-sm transition hover:bg-white dark:border-default-600 dark:bg-default-800 dark:text-default-200"
							onClick={closeDayPanel}
							aria-label={i18n.t("googleAgenda.closeDayPanel")}
						>
							✕
						</button>
					</div>
				</div>

				<div className="flex-1 overflow-y-auto px-5 py-4">
					{dayEvents.length === 0 ? (
						<div className="rounded-2xl border border-dashed border-default-200 bg-default-50/80 px-4 py-10 text-center dark:border-default-700 dark:bg-default-800/30">
							<p className="m-0 text-sm text-default-500">
								{i18n.t("googleAgenda.noEvents")}
							</p>
						</div>
					) : (
						<ul className="m-0 flex list-none flex-col gap-3 p-0">
							{dayEvents.map((ev) => (
								<li
									key={ev.id}
									className="rounded-2xl border border-default-200 bg-white p-4 shadow-sm transition hover:border-primary/30 hover:shadow-md dark:border-default-700 dark:bg-default-800/60"
									style={{
										borderLeftWidth: 3,
										borderLeftColor: "var(--color-primary, #3b82f6)",
									}}
								>
									<p className="m-0 text-xs font-semibold text-primary">
										{formatEventTime(ev)}
									</p>
									<p className="mt-1 mb-0 text-[15px] font-semibold text-default-900 dark:text-default-50">
										{ev.summary || "—"}
									</p>
									{ev.description ? (
										<p className="mt-1.5 mb-0 line-clamp-3 text-sm text-default-500">
											{ev.description}
										</p>
									) : null}
									<div className="mt-3 flex gap-2">
										<button
											type="button"
											className="rounded-lg border border-default-200 px-2.5 py-1.5 text-xs font-medium text-default-700 transition hover:bg-default-50 dark:border-default-600 dark:text-default-200 dark:hover:bg-default-700"
											onClick={() => openEdit(ev)}
										>
											{i18n.t("googleAgenda.editEvent")}
										</button>
										<button
											type="button"
											className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-danger transition hover:bg-danger/10"
											onClick={() => setDeleteTarget(ev)}
										>
											{i18n.t("googleAgenda.delete")}
										</button>
									</div>
								</li>
							))}
						</ul>
					)}
				</div>

				<div className="border-t border-default-200 bg-default-50/80 p-4 dark:border-default-700 dark:bg-default-950/40">
					<button
						type="button"
						className="btn w-full bg-primary text-white shadow-md shadow-primary/20"
						onClick={() => openCreate(selectedDay)}
					>
						{i18n.t("googleAgenda.newEvent")}
					</button>
				</div>
			</aside>

			<AppModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				title={
					editing
						? i18n.t("googleAgenda.editEvent")
						: i18n.t("googleAgenda.newEvent")
				}
				footer={
					<div className="flex flex-wrap justify-end gap-2">
						<button
							type="button"
							className="btn btn-sub-gray"
							onClick={() => setModalOpen(false)}
							disabled={saving}
						>
							{i18n.t("googleAgenda.cancel")}
						</button>
						<button
							type="button"
							className="btn bg-primary text-white"
							onClick={handleSave}
							disabled={saving || !form.summary.trim()}
						>
							{i18n.t("googleAgenda.save")}
						</button>
					</div>
				}
			>
				<div className="space-y-4">
					<label className="block">
						<span className="mb-2 block text-sm font-medium">
							{i18n.t("googleAgenda.summary")}
						</span>
						<input
							className="form-input w-full"
							value={form.summary}
							onChange={(e) =>
								setForm((f) => ({ ...f, summary: e.target.value }))
							}
						/>
					</label>
					<label className="block">
						<span className="mb-2 block text-sm font-medium">
							{i18n.t("googleAgenda.description")}
						</span>
						<textarea
							className="form-input w-full min-h-[80px]"
							value={form.description}
							onChange={(e) =>
								setForm((f) => ({ ...f, description: e.target.value }))
							}
						/>
					</label>
					<div className="block space-y-2">
						<span className="mb-2 block text-sm font-medium">
							{i18n.t("googleAgenda.contact")}
						</span>
						<input
							className="form-input w-full"
							placeholder={i18n.t("googleAgenda.contactSearch")}
							value={contactQuery}
							onChange={(e) => {
								const nextValue = e.target.value;
								setContactQuery(nextValue);
								setForm((f) => ({
									...f,
									contactId:
										renderContactLabel(f.contact) === nextValue
											? f.contactId
											: null,
									contact:
										renderContactLabel(f.contact) === nextValue
											? f.contact
											: null,
								}));
							}}
						/>
						<p className="m-0 text-xs text-default-500 dark:text-default-400">
							{i18n.t("googleAgenda.contactHint")}
						</p>
						{contactQuery.trim().length > 0 &&
							contactQuery.trim().length < 3 && (
								<p className="m-0 text-sm text-default-500 dark:text-default-400">
									{i18n.t("googleAgenda.contactSearchHint")}
								</p>
							)}
						{loadingContacts && (
							<div className="inline-flex items-center gap-2 text-sm text-default-500 dark:text-default-400">
								<div className="app-modal-spinner !size-4" />
								<span>{i18n.t("googleAgenda.contactSearch")}</span>
							</div>
						)}
						{!loadingContacts &&
							contactQuery.trim().length >= 3 &&
							renderContactLabel(form.contact).toLowerCase() !==
								contactQuery.trim().toLowerCase() &&
							contactOptions.length > 0 && (
								<div className="max-h-56 overflow-y-auto rounded-md border border-default-200 dark:border-default-700">
									{contactOptions.map((contactOption) => (
										<button
											key={contactOption.id}
											type="button"
											className="flex w-full flex-col gap-0.5 border-b border-default-200 px-3 py-2 text-start last:border-b-0 hover:bg-default-100 dark:border-default-700 dark:hover:bg-default-800"
											onClick={() => {
												setForm((f) => ({
													...f,
													contactId: contactOption.id,
													contact: contactOption,
												}));
												setContactQuery(renderContactLabel(contactOption));
												setContactOptions([]);
											}}
										>
											<span className="text-sm font-medium text-default-800 dark:text-default-100">
												{contactOption.name}
											</span>
											<span className="text-xs text-default-500 dark:text-default-400">
												{renderContactLabel(contactOption)}
											</span>
										</button>
									))}
								</div>
							)}
						{!loadingContacts &&
							contactQuery.trim().length >= 3 &&
							!form.contactId &&
							contactOptions.length === 0 && (
								<p className="m-0 text-sm text-default-500 dark:text-default-400">
									{i18n.t("googleAgenda.contactEmpty")}
								</p>
							)}
						{form.contactId && form.contact && (
							<div className="flex items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/10 px-3 py-2">
								<div className="min-w-0">
									<p className="m-0 truncate text-sm font-medium text-default-800 dark:text-default-100">
										{form.contact.name}
									</p>
									<p className="m-0 truncate text-xs text-default-500 dark:text-default-400">
										{form.contact.number || "—"}
									</p>
								</div>
								<button
									type="button"
									className="btn btn-sm btn-sub-gray shrink-0"
									onClick={() => {
										setForm((f) => ({
											...f,
											contactId: null,
											contact: null,
										}));
										setContactQuery("");
										setContactOptions([]);
									}}
								>
									{i18n.t("googleAgenda.contactClear")}
								</button>
							</div>
						)}
					</div>
					<label className="flex items-center gap-2">
						<input
							type="checkbox"
							checked={form.allDay}
							onChange={(e) =>
								setForm((f) => ({ ...f, allDay: e.target.checked }))
							}
						/>
						<span className="text-sm">{i18n.t("googleAgenda.allDay")}</span>
					</label>
					{form.allDay ? (
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
							<label className="block">
								<span className="mb-2 block text-sm font-medium">
									{i18n.t("googleAgenda.start")}
								</span>
								<input
									type="date"
									className="form-input w-full"
									value={form.startDate}
									onChange={(e) =>
										setForm((f) => ({ ...f, startDate: e.target.value }))
									}
								/>
							</label>
							<label className="block">
								<span className="mb-2 block text-sm font-medium">
									{i18n.t("googleAgenda.end")}
								</span>
								<input
									type="date"
									className="form-input w-full"
									value={form.endDate}
									onChange={(e) =>
										setForm((f) => ({ ...f, endDate: e.target.value }))
									}
								/>
							</label>
						</div>
					) : (
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
							<label className="block">
								<span className="mb-2 block text-sm font-medium">
									{i18n.t("googleAgenda.start")}
								</span>
								<input
									type="datetime-local"
									className="form-input w-full"
									value={form.start}
									onChange={(e) =>
										setForm((f) => ({ ...f, start: e.target.value }))
									}
								/>
							</label>
							<label className="block">
								<span className="mb-2 block text-sm font-medium">
									{i18n.t("googleAgenda.end")}
								</span>
								<input
									type="datetime-local"
									className="form-input w-full"
									value={form.end}
									onChange={(e) =>
										setForm((f) => ({ ...f, end: e.target.value }))
									}
								/>
							</label>
						</div>
					)}
				</div>
			</AppModal>

			<AppModal
				open={settingsOpen}
				onClose={() => setSettingsOpen(false)}
				title={i18n.t("googleAgenda.settingsTitle")}
				maxWidth={notifyEnabled && notifyIsBusiness ? 980 : 560}
				footer={
					<div className="flex flex-wrap justify-end gap-2">
						<button
							type="button"
							className="btn btn-sub-gray"
							onClick={() => setSettingsOpen(false)}
							disabled={savingSettings}
						>
							{i18n.t("googleAgenda.cancel")}
						</button>
						<button
							type="button"
							className="btn bg-primary text-white"
							onClick={handleSaveSettings}
							disabled={savingSettings}
						>
							{i18n.t("googleAgenda.settingsSave")}
						</button>
					</div>
				}
			>
				<p className="mb-4 mt-0 text-sm text-default-500">
					{i18n.t("googleAgenda.settingsHint")}
				</p>
				<div className="space-y-4">
					<label className="block">
						<span className="mb-2 block text-sm font-medium">
							{i18n.t("googleAgenda.slotMinutes")}
						</span>
						<select
							className="form-input w-full"
							value={slotMinutes}
							onChange={(e) => setSlotMinutes(Number(e.target.value))}
						>
							{SLOT_OPTIONS.map((m) => (
								<option key={m} value={m}>
									{m} {i18n.t("googleAgenda.minutesSuffix")}
								</option>
							))}
						</select>
						<span className="mt-1 block text-xs text-default-400">
							{i18n.t("googleAgenda.slotMinutesHint")}
						</span>
					</label>
					<label className="block">
						<span className="mb-2 block text-sm font-medium">
							{i18n.t("googleAgenda.slotCapacity")}
						</span>
						<input
							type="number"
							min={1}
							max={50}
							className="form-input w-full"
							value={slotCapacity}
							onChange={(e) =>
								setSlotCapacity(Math.max(1, Math.min(50, Number(e.target.value) || 1)))
							}
						/>
						<span className="mt-1 block text-xs text-default-400">
							{i18n.t("googleAgenda.slotCapacityHint")}
						</span>
					</label>
					<label className="block">
						<span className="mb-2 block text-sm font-medium">
							{i18n.t("googleAgenda.minAdvance")}
						</span>
						<select
							className="form-input w-full"
							value={minAdvanceMinutes}
							onChange={(e) => setMinAdvanceMinutes(Number(e.target.value))}
						>
							{ADVANCE_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{i18n.t(`googleAgenda.${opt.labelKey}`)}
								</option>
							))}
						</select>
						<span className="mt-1 block text-xs text-default-400">
							{i18n.t("googleAgenda.minAdvanceHint")}
						</span>
					</label>
					<div className="border-t border-default-200 pt-4 dark:border-default-700">
						<label className="flex items-start gap-2">
							<input
								type="checkbox"
								className="mt-1"
								checked={notifyEnabled}
								onChange={(e) => setNotifyEnabled(e.target.checked)}
							/>
							<span>
								<span className="block text-sm font-medium">
									{i18n.t("googleAgenda.notifyEnabled")}
								</span>
								<span className="mt-0.5 block text-xs text-default-400">
									{i18n.t("googleAgenda.notifyHint")}
								</span>
							</span>
						</label>
						{notifyEnabled ? (
							<div className="mt-4 space-y-4">
								<label className="block">
									<span className="mb-2 block text-sm font-medium">
										{i18n.t("googleAgenda.notifyWhen")}
									</span>
									<select
										className="form-input w-full"
										value={notifyOffsetMinutes}
										onChange={(e) =>
											setNotifyOffsetMinutes(Number(e.target.value))
										}
									>
										{NOTIFY_OFFSET_OPTIONS.map((opt) => (
											<option key={opt.value} value={opt.value}>
												{i18n.t(`googleAgenda.${opt.labelKey}`)}
											</option>
										))}
									</select>
								</label>
								<label className="block">
									<span className="mb-2 block text-sm font-medium">
										{i18n.t("googleAgenda.notifyConnection")}
									</span>
									<select
										className="form-input w-full"
										value={notifyConnectionId}
										onChange={(e) => {
											setNotifyConnectionId(e.target.value);
											setNotifyTemplateId("");
											setNotifyTemplate(null);
											setNotifyTemplateParams([]);
											setNotifyMediaPath("");
											setTemplateParamsLength(0);
										}}
									>
										<option value="">
											{i18n.t("googleAgenda.notifyConnectionEmpty")}
										</option>
										{whatsappConnections.map((conn) => (
											<option key={conn.id} value={conn.id}>
												{conn.name || `#${conn.id}`}
											</option>
										))}
									</select>
									<span className="mt-1 block text-xs text-default-400">
										{i18n.t("googleAgenda.notifyConnectionHint")}
									</span>
								</label>
								{notifyConnectionId && notifyIsBusiness ? (
									<div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(280px,0.85fr)] lg:items-start">
										<div className="min-w-0 space-y-3">
											<TemplatesTable
												whatsappId={notifyConnectionId}
												scheduleData={{ templateId: notifyTemplateId }}
												setScheduleData={(next) => {
													const nextId = next?.templateId || "";
													if (String(nextId) !== String(notifyTemplateId)) {
														setNotifyMediaPath("");
														setNotifyTemplateParams([]);
													}
													setNotifyTemplateId(nextId);
													if (next?.template) {
														setNotifyTemplate(next.template);
													}
												}}
												setTemplateParamsLength={setTemplateParamsLength}
												onTemplateResolved={handleNotifyTemplateResolved}
											/>
											{notifyRequiresMedia ? (
												<div className="space-y-2 rounded-lg border border-default-200 p-3 dark:border-default-700">
													<span className="block text-sm font-medium">
														{i18n.t("googleAgenda.notifyMedia")} ({notifyMediaLabel})
													</span>
													<div className="flex flex-col gap-2 sm:flex-row">
														<input
															type="url"
															className="form-input min-w-0 flex-1"
															placeholder={i18n.t(
																"googleAgenda.notifyMediaUrlPlaceholder"
															)}
															value={notifyMediaPath || ""}
															onChange={(e) =>
																setNotifyMediaPath(
																	e.target.value.trim() ? e.target.value : ""
																)
															}
														/>
														<div className="flex shrink-0 gap-2">
															{notifyMediaPath ? (
																<button
																	type="button"
																	className="btn btn-sub-gray"
																	onClick={() => setNotifyMediaPath("")}
																>
																	{i18n.t("googleAgenda.notifyRemoveMedia")}
																</button>
															) : null}
															<label className="btn btn-primary cursor-pointer">
																{uploadingNotifyMedia
																	? i18n.t("googleAgenda.notifyUploadingMedia")
																	: i18n.t("googleAgenda.notifyAttachMedia")}
																<input
																	type="file"
																	className="sr-only"
																	accept={
																		notifyHeaderFormat === "IMAGE"
																			? "image/png,image/jpeg,image/jpg,image/webp"
																			: notifyHeaderFormat === "VIDEO"
																				? "video/mp4"
																				: "application/pdf"
																	}
																	disabled={uploadingNotifyMedia}
																	onChange={handleNotifyMediaUpload}
																/>
															</label>
														</div>
													</div>
													{notifyHeaderFormat === "IMAGE" && notifyMediaPath ? (
														<img
															src={notifyMediaPath}
															alt=""
															className="mt-1 max-h-28 rounded-lg object-cover"
														/>
													) : null}
												</div>
											) : null}
											{notifyParamSlots.length > 0
												? notifyParamSlots.map((slot, index) => (
														<label key={`${slot.kind}-${index}`} className="block">
															<span className="mb-2 block text-sm font-medium">
																{slot.label}
															</span>
															<input
																className="form-input w-full"
																value={notifyTemplateParams[index] || ""}
																onChange={(e) => {
																	const copy = [...notifyTemplateParams];
																	copy[index] = e.target.value;
																	setNotifyTemplateParams(copy);
																}}
															/>
														</label>
												  ))
												: null}
										</div>
										<aside className="lg:sticky lg:top-0">
											<TemplateConversationPreview
												template={notifyTemplate}
												params={notifyTemplateParams}
												mediaPath={notifyMediaPath || null}
												darkMode={darkMode}
											/>
										</aside>
									</div>
								) : null}
								{notifyConnectionId && !notifyIsBusiness ? (
									<label className="block">
										<span className="mb-2 block text-sm font-medium">
											{i18n.t("googleAgenda.notifyBody")}
										</span>
										<textarea
											className="form-input w-full min-h-[90px]"
											value={notifyBody}
											onChange={(e) => setNotifyBody(e.target.value)}
											placeholder={i18n.t("googleAgenda.notifyBodyPlaceholder")}
										/>
									</label>
								) : null}
							</div>
						) : null}
					</div>
				</div>
			</AppModal>

			<ConfirmationModal
				title={i18n.t("googleAgenda.deleteTitle")}
				open={Boolean(deleteTarget)}
				onClose={() => setDeleteTarget(null)}
				onConfirm={handleDelete}
			>
				{i18n.t("googleAgenda.deleteConfirm")}
			</ConfirmationModal>
		</div>
	);
}
